import { useCallback } from 'react';
import type { WritableUtmParameters } from '@shared/utils/utmParameters';
import { useUtmParameters } from '@shared/utils/utmParameters';
import { resolveInternalNavUtm, useRawUtmMedium } from './internalNavUtm';

/** Shared `utm_source` for every guest-site nav surface, so Amplitude buckets them together. */
export const GUEST_SITE_NAV_UTM_SOURCE = 'guest_site_nav';

/**
 * Resolves the UTM a guest-site nav link should carry, per page slug.
 *
 * Returns a function rather than an object because nav links are built inside a `pages.map()`:
 * one hook call per page would change count whenever a host shows or hides a page.
 *
 * Real acquisition UTM always wins — see {@link resolveInternalNavUtm}, which also restores a raw
 * `utm_medium` the `UTM_MEDIUMS` allowlist would otherwise drop. The single returned object powers
 * both the link's href and the click telemetry, so the two can never disagree.
 */
export const useGuestSiteNavUtm = (campaignPrefix: string) => {
  const currentUtm = useUtmParameters();
  const rawUtmMedium = useRawUtmMedium();

  return useCallback(
    (pageSlug: string): WritableUtmParameters =>
      resolveInternalNavUtm(
        currentUtm,
        {
          utm_medium: 'web',
          utm_source: GUEST_SITE_NAV_UTM_SOURCE,
          utm_campaign: `${campaignPrefix}_${pageSlug}`,
        },
        rawUtmMedium,
      ),
    [currentUtm, rawUtmMedium, campaignPrefix],
  );
};
